export type TenantOverrideState = {
  tenantId: string;
  organizationId: string;
};

export type SavedTenantConfig = {
  id: string;
  tenantId: string;
  label: string;
  createdAt: string;
  updatedAt: string;
};

type TenantOverrideListener = (state: TenantOverrideState | null) => void;

const tenantOverrideStorageKey = 'tenant_override_state';
const savedTenantConfigsStorageKey = 'saved_tenant_configs';
const recentTenantIdsStorageKey = 'recent_tenant_ids';
const maxRecentTenantIds = 8;
const listeners = new Set<TenantOverrideListener>();

const canUseStorage = () => typeof window !== 'undefined' && typeof localStorage !== 'undefined';

const normalizeValue = (value: unknown) => {
  if (typeof value !== 'string') {
    return '';
  }

  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return '';
  }

  const lowered = trimmed.toLowerCase();
  if (lowered === 'null' || lowered === 'undefined') {
    return '';
  }

  return trimmed;
};

const dedupeTenantIds = (values: string[]) => {
  const seen = new Set<string>();
  return values.filter((value) => {
    const normalized = normalizeValue(value);
    if (!normalized || seen.has(normalized)) {
      return false;
    }
    seen.add(normalized);
    return true;
  });
};

const buildTenantConfigId = () => {
  const randomId = globalThis.crypto?.randomUUID?.();
  if (typeof randomId === 'string' && randomId.trim().length > 0) {
    return randomId;
  }

  return `tenant-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const resolveTenantLabel = (label: unknown, tenantId: string) => {
  const normalizedLabel = normalizeValue(label);
  if (normalizedLabel.length > 0) {
    return normalizedLabel;
  }

  const shortenedTenantId = tenantId.length > 12 ? `${tenantId.slice(0, 12)}...` : tenantId;
  return `Tenant ${shortenedTenantId}`;
};

const readJson = <T>(key: string): T | null => {
  if (!canUseStorage()) {
    return null;
  }

  const raw = localStorage.getItem(key);
  if (!raw) {
    return null;
  }

  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
};

const notifyListeners = () => {
  const nextState = getTenantOverrideState();
  listeners.forEach((listener) => {
    listener(nextState);
  });
};

const persistRecentTenantIds = (tenantIds: string[]) => {
  if (!canUseStorage()) {
    return;
  }

  const nextRecentTenantIds = dedupeTenantIds(tenantIds).slice(0, maxRecentTenantIds);
  localStorage.setItem(recentTenantIdsStorageKey, JSON.stringify(nextRecentTenantIds));
};

const normalizeSavedTenantConfig = (
  candidate: Partial<SavedTenantConfig> | null | undefined
): SavedTenantConfig | null => {
  const tenantId = normalizeValue(candidate?.tenantId);
  if (!tenantId) {
    return null;
  }

  const id = normalizeValue(candidate?.id) || buildTenantConfigId();
  const label = resolveTenantLabel(candidate?.label, tenantId);
  const now = new Date().toISOString();
  const createdAt = normalizeValue(candidate?.createdAt) || now;
  const updatedAt = normalizeValue(candidate?.updatedAt) || createdAt;

  return {
    id,
    tenantId,
    label,
    createdAt,
    updatedAt,
  };
};

const persistSavedTenantConfigs = (tenantConfigs: SavedTenantConfig[]) => {
  if (!canUseStorage()) {
    return;
  }

  localStorage.setItem(savedTenantConfigsStorageKey, JSON.stringify(tenantConfigs));
};

export const getTenantOverrideState = (): TenantOverrideState | null => {
  const parsed = readJson<Partial<TenantOverrideState>>(tenantOverrideStorageKey);
  if (!parsed) {
    return null;
  }

  const tenantId = normalizeValue(parsed.tenantId);
  if (!tenantId) {
    return null;
  }

  const organizationId = normalizeValue(parsed.organizationId) || tenantId;
  return {
    tenantId,
    organizationId,
  };
};

export const loadRecentTenantIds = (): string[] => {
  const parsed = readJson<unknown>(recentTenantIdsStorageKey);
  if (!Array.isArray(parsed)) {
    return [];
  }

  return dedupeTenantIds(
    parsed.filter((entry): entry is string => typeof entry === 'string')
  );
};

export const loadSavedTenantConfigs = (): SavedTenantConfig[] => {
  const parsed = readJson<unknown>(savedTenantConfigsStorageKey);
  if (!Array.isArray(parsed)) {
    return [];
  }

  const configs = parsed
    .map((entry) =>
      normalizeSavedTenantConfig(
        entry && typeof entry === 'object' ? (entry as Partial<SavedTenantConfig>) : null
      )
    )
    .filter((entry): entry is SavedTenantConfig => entry != null);

  const seenIds = new Set<string>();
  return configs
    .filter((entry) => {
      if (seenIds.has(entry.id)) {
        return false;
      }
      seenIds.add(entry.id);
      return true;
    })
    .sort((left, right) => right.updatedAt.localeCompare(left.updatedAt));
};

export const setTenantOverrideState = (nextState: Partial<TenantOverrideState>) => {
  if (!canUseStorage()) {
    return;
  }

  const tenantId = normalizeValue(nextState.tenantId);
  if (!tenantId) {
    clearTenantOverrideState();
    return;
  }

  const organizationId = normalizeValue(nextState.organizationId) || tenantId;
  localStorage.setItem(
    tenantOverrideStorageKey,
    JSON.stringify({
      tenantId,
      organizationId,
    } satisfies TenantOverrideState)
  );
  persistRecentTenantIds([tenantId, ...loadRecentTenantIds()]);
  notifyListeners();
};

export const saveTenantConfig = (nextConfig: {
  id?: string;
  tenantId: string;
  label?: string;
}): SavedTenantConfig | null => {
  if (!canUseStorage()) {
    return null;
  }

  const tenantId = normalizeValue(nextConfig.tenantId);
  if (!tenantId) {
    return null;
  }

  const existingConfigs = loadSavedTenantConfigs();
  const matchingConfig =
    existingConfigs.find((entry) => entry.id === nextConfig.id) ??
    existingConfigs.find((entry) => entry.tenantId === tenantId);
  const now = new Date().toISOString();
  const savedConfig = normalizeSavedTenantConfig({
    ...matchingConfig,
    id: matchingConfig?.id ?? nextConfig.id,
    tenantId,
    label: nextConfig.label,
    createdAt: matchingConfig?.createdAt ?? now,
    updatedAt: now,
  });

  if (!savedConfig) {
    return null;
  }

  const remainingConfigs = existingConfigs.filter((entry) => entry.id !== savedConfig.id);
  const nextConfigs = [savedConfig, ...remainingConfigs].sort((left, right) =>
    right.updatedAt.localeCompare(left.updatedAt)
  );
  persistSavedTenantConfigs(nextConfigs);
  persistRecentTenantIds([tenantId, ...loadRecentTenantIds()]);
  notifyListeners();
  return savedConfig;
};

export const removeSavedTenantConfig = (id: string) => {
  if (!canUseStorage()) {
    return;
  }

  const normalizedId = normalizeValue(id);
  if (!normalizedId) {
    return;
  }

  const nextConfigs = loadSavedTenantConfigs().filter((entry) => entry.id !== normalizedId);
  persistSavedTenantConfigs(nextConfigs);
  notifyListeners();
};

export const clearTenantOverrideState = () => {
  if (!canUseStorage()) {
    return;
  }

  localStorage.removeItem(tenantOverrideStorageKey);
  notifyListeners();
};

export const removeRecentTenantId = (tenantId: string) => {
  if (!canUseStorage()) {
    return;
  }

  const normalizedTenantId = normalizeValue(tenantId);
  if (!normalizedTenantId) {
    return;
  }

  persistRecentTenantIds(
    loadRecentTenantIds().filter((entry) => entry !== normalizedTenantId)
  );
  notifyListeners();
};

export const subscribeTenantOverrideState = (listener: TenantOverrideListener) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};
